/**
 * SFrame160 — Personal Belongings & Digital Assets (secure-inventory si-5)
 * Consolidated inventory page: tiles → modal → unified list.
 * Business interests & valuations: /sframe-161/:estateId (SFrame160Business)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { SettlementStepPage } from '../../settlement';
import Modal from '../../ui/Modal';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  migrateSFrame160Data,
  PROPERTY_ITEM_TYPES,
  type PropertyAccessStatus,
  type PropertyAppraisalStatus,
  type PropertyExecutorStatus,
  type PropertyIntendedAction,
  type PropertyItemType,
  type PropertyValuationMethod,
  type SFrame160Persisted,
  type UnifiedPropertyItem,
} from './sframe160Persisted';

const step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-5')!;

const nid = () => `n-${Math.random().toString(36).slice(2, 9)}`;

/* ---------- tile definitions ---------- */

interface TileGroup {
  label: string;
  tiles: { type: PropertyItemType; label: string }[];
}

const TILE_GROUPS: TileGroup[] = [
  {
    label: 'Personal Belongings',
    tiles: [
      { type: 'physical_assets', label: 'Physical Assets' },
      { type: 'collectibles_valuables', label: 'Collectibles & Valuables' },
      { type: 'electronics', label: 'Electronics' },
    ],
  },
  {
    label: 'Digital Assets',
    tiles: [
      { type: 'digital_assets', label: 'Digital Assets' },
      { type: 'custom', label: 'Add Custom Item' },
    ],
  },
];

/* ---------- dropdown options ---------- */

const VALUATION_METHODS: PropertyValuationMethod[] = ['Estimated', 'Market Value', 'Appraised', 'Unknown'];
const APPRAISAL_STATUSES: PropertyAppraisalStatus[] = ['Not Started', 'In Progress', 'Completed'];
const ACCESS_STATUSES: PropertyAccessStatus[] = ['Has access', 'No access', 'Unknown'];
const INTENDED_ACTIONS: PropertyIntendedAction[] = ['Retain', 'Distribute', 'Transfer', 'Sell', 'Close', 'Memorialize', 'Unknown'];
const EXECUTOR_STATUSES: PropertyExecutorStatus[] = ['Needs review', 'In Progress', 'Completed'];

/* ---------- helpers ---------- */

function emptyItem(type: PropertyItemType): UnifiedPropertyItem {
  return {
    id: nid(),
    type,
    name: '',
    description: '',
    estimatedValue: '',
    valuationMethod: 'Unknown',
    needsAppraisal: false,
    appraisalStatus: 'Not Started',
    location: '',
    handlingInstructions: '',
    notes: '',
    accessStatus: 'Unknown',
    intendedAction: 'Unknown',
    executorProgressStatus: 'Needs review',
  };
}

function typeLabel(type: PropertyItemType): string {
  return PROPERTY_ITEM_TYPES.find((t) => t.value === type)?.label ?? type;
}

function formatMoney(s: string): string {
  const n = Number(String(s).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function statusColor(status: PropertyExecutorStatus): string {
  if (status === 'Completed') return 'bg-emerald-50 text-emerald-800';
  if (status === 'In Progress') return 'bg-cyan-50 text-cyan-800';
  return 'bg-stone-100 text-stone-700';
}

/* ---------- component ---------- */

export default function SFrame160Property() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId ?? null, { readOnly: isArchived });

  const mergedRef = useRef<SFrame160Persisted>(migrateSFrame160Data(undefined));

  const [items, setItems] = useState<UnifiedPropertyItem[]>([]);
  const [hydrated, setHydrated] = useState(!estateId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<UnifiedPropertyItem | null>(null);

  /* ---- load ---- */
  useEffect(() => {
    if (!estateId) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const fd = await loadFrameData('sframe-160');
      if (cancelled) return;
      const m = migrateSFrame160Data(fd?.data as Record<string, unknown> | undefined);
      mergedRef.current = m;
      setItems(m.items);
      setHydrated(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  /* ---- auto-save ---- */
  useEffect(() => {
    if (!estateId || !hydrated || isArchived) return;
    const t = window.setTimeout(() => {
      const base = mergedRef.current;
      const next = { ...base, items, personalItems: [], digitalAssets: [] };
      mergedRef.current = next;
      void saveFrameData('sframe-160', next);
    }, 600);
    return () => window.clearTimeout(t);
  }, [items, estateId, hydrated, saveFrameData, isArchived]);

  /* ---- CRUD ---- */
  const openAdd = (type: PropertyItemType) => {
    setEditingItem(emptyItem(type));
    setModalOpen(true);
  };

  const openEdit = (item: UnifiedPropertyItem) => {
    setEditingItem({ ...item });
    setModalOpen(true);
  };

  const saveModal = () => {
    if (!editingItem) return;
    setItems((rows) => {
      const exists = rows.some((r) => r.id === editingItem.id);
      if (exists) return rows.map((r) => (r.id === editingItem.id ? editingItem : r));
      return [...rows, editingItem];
    });
    setModalOpen(false);
    setEditingItem(null);
  };

  const deleteItem = (id: string) => {
    setItems((rows) => rows.filter((r) => r.id !== id));
  };

  const patch = useCallback((p: Partial<UnifiedPropertyItem>) => {
    setEditingItem((row) => (row ? { ...row, ...p } : row));
  }, []);

  /* ---- derived ---- */
  const totalValue = items.reduce((sum, it) => {
    const n = Number(String(it.estimatedValue).replace(/[^0-9.-]/g, ''));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  const isEdit = editingItem ? items.some((r) => r.id === editingItem.id) : false;

  /* ---- render ---- */
  return (
    <SettlementStepPage
      step={step}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      hidePlannerBanner
      settlementDocumentContext={{ phase: 'secure-inventory', stepId: step.id }}
    >
      {/* Business interests link */}
      {estateId ? (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
          <span>
            Items subtotal (this page):{' '}
            <strong className="text-stone-900">
              {totalValue > 0
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(totalValue)
                : '—'}
            </strong>
          </span>
          <Link
            to={`/sframe-161/${estateId}`}
            className="font-semibold text-cyan-700 hover:text-cyan-800 hover:underline"
          >
            Business interests &amp; valuations &rarr;
          </Link>
        </div>
      ) : null}

      {/* Callout */}
      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        High-value or unique items (e.g., collectibles, jewelry, art) may require appraisal.
      </div>

      {/* Category tiles */}
      {TILE_GROUPS.map((group) => (
        <div key={group.label} className="mb-4">
          <h3 className="mb-2 text-sm font-semibold text-stone-600">{group.label}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-2">
            {group.tiles.map((tile) => {
              const count = items.filter((it) => it.type === tile.type).length;
              return (
                <button
                  key={tile.type}
                  type="button"
                  disabled={isArchived}
                  onClick={() => openAdd(tile.type)}
                  className="flex flex-col items-start rounded-xl border border-stone-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-cyan-400 hover:bg-cyan-50 disabled:opacity-50"
                >
                  <span className="text-sm font-semibold text-stone-800">{tile.label}</span>
                  <span className="mt-1 text-xs text-stone-500">
                    {count === 0 ? 'None added' : `${count} added`}
                  </span>
                  <span className="mt-1 text-xs font-medium text-cyan-700">+ Add</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Unified items list */}
      <section className="mt-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-900">All Items</h2>

        {items.length === 0 ? (
          <p className="text-sm text-stone-400 italic py-4">
            No items yet. Use the tiles above to add personal belongings or digital assets.
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-stone-200 bg-stone-50/50 p-4"
              >
                {/* header row */}
                <div className="flex justify-between gap-2 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-stone-900">{item.name || 'Untitled item'}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{typeLabel(item.type)}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={isArchived}
                      aria-label="Edit"
                      onClick={() => openEdit(item)}
                      className="rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-stone-800 disabled:opacity-50"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      disabled={isArchived}
                      aria-label="Delete"
                      onClick={() => deleteItem(item.id)}
                      className="rounded p-1 text-stone-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* detail grid */}
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                  {item.location ? (
                    <div>
                      <dt className="text-stone-500">Location</dt>
                      <dd className="text-stone-800">{item.location}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-stone-500">Est. Value</dt>
                    <dd className="text-stone-800">{formatMoney(item.estimatedValue)}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-500">Valuation</dt>
                    <dd className="text-stone-800">{item.valuationMethod}</dd>
                  </div>
                  {item.needsAppraisal ? (
                    <div>
                      <dt className="text-stone-500">Appraisal</dt>
                      <dd className="text-stone-800">{item.appraisalStatus}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-stone-500">Access</dt>
                    <dd className="text-stone-800">{item.accessStatus}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-500">Action</dt>
                    <dd className="text-stone-800">{item.intendedAction}</dd>
                  </div>
                </dl>

                {/* executor progress badge */}
                <div className="mt-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(item.executorProgressStatus)}`}>
                    {item.executorProgressStatus}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- Modal ---- */}
      <Modal
        open={modalOpen}
        title={isEdit ? 'Edit Item' : 'Add Item'}
        onCancel={() => {
          setModalOpen(false);
          setEditingItem(null);
        }}
        onOk={saveModal}
        okText="Save"
        containerClassName="max-w-lg"
      >
        {editingItem ? (
          <div className="space-y-3 text-sm">
            {/* Type */}
            <label className="block text-stone-700">
              Type
              <select
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                value={editingItem.type}
                onChange={(e) => patch({ type: e.target.value as PropertyItemType })}
                disabled={isArchived}
              >
                {PROPERTY_ITEM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>

            {/* Item Name */}
            <label className="block text-stone-700">
              Item Name
              <input
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5"
                value={editingItem.name}
                onChange={(e) => patch({ name: e.target.value })}
                disabled={isArchived}
              />
            </label>

            {/* Description */}
            <label className="block text-stone-700">
              Description
              <textarea
                className="mt-1 w-full min-h-[64px] rounded-md border border-stone-200 px-2 py-1.5"
                value={editingItem.description}
                onChange={(e) => patch({ description: e.target.value })}
                disabled={isArchived}
              />
            </label>

            {/* Estimated Value */}
            <label className="block text-stone-700">
              Estimated Value
              <input
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5"
                value={editingItem.estimatedValue}
                onChange={(e) => patch({ estimatedValue: e.target.value })}
                placeholder="$ amount"
                disabled={isArchived}
              />
            </label>

            {/* Valuation Method */}
            <label className="block text-stone-700">
              Valuation Method
              <select
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                value={editingItem.valuationMethod}
                onChange={(e) => patch({ valuationMethod: e.target.value as PropertyValuationMethod })}
                disabled={isArchived}
              >
                {VALUATION_METHODS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </label>

            {/* Needs Appraisal */}
            <label className="flex items-center gap-2 text-stone-700">
              <input
                type="checkbox"
                className="rounded border-stone-300"
                checked={editingItem.needsAppraisal}
                onChange={(e) => patch({ needsAppraisal: e.target.checked })}
                disabled={isArchived}
              />
              Needs Appraisal
            </label>

            {/* Appraisal Status (conditional) */}
            {editingItem.needsAppraisal ? (
              <label className="block text-stone-700">
                Appraisal Status
                <select
                  className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                  value={editingItem.appraisalStatus}
                  onChange={(e) => patch({ appraisalStatus: e.target.value as PropertyAppraisalStatus })}
                  disabled={isArchived}
                >
                  {APPRAISAL_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            ) : null}

            {/* Location */}
            <label className="block text-stone-700">
              Location
              <input
                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5"
                value={editingItem.location}
                onChange={(e) => patch({ location: e.target.value })}
                disabled={isArchived}
              />
            </label>

            {/* Handling Instructions */}
            <label className="block text-stone-700">
              Handling Instructions
              <textarea
                className="mt-1 w-full min-h-[48px] rounded-md border border-stone-200 px-2 py-1.5"
                value={editingItem.handlingInstructions}
                onChange={(e) => patch({ handlingInstructions: e.target.value })}
                disabled={isArchived}
              />
            </label>

            {/* Notes */}
            <label className="block text-stone-700">
              Notes
              <textarea
                className="mt-1 w-full min-h-[48px] rounded-md border border-stone-200 px-2 py-1.5"
                value={editingItem.notes}
                onChange={(e) => patch({ notes: e.target.value })}
                disabled={isArchived}
              />
            </label>

            {/* --- Settlement-only fields --- */}
            <div className="border-t border-stone-200 pt-3 mt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
                Settlement Fields
              </p>

              {/* Access Status */}
              <label className="block text-stone-700 mb-3">
                Access Status
                <select
                  className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                  value={editingItem.accessStatus}
                  onChange={(e) => patch({ accessStatus: e.target.value as PropertyAccessStatus })}
                  disabled={isArchived}
                >
                  {ACCESS_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>

              {/* Intended Action */}
              <label className="block text-stone-700 mb-3">
                Intended Action
                <select
                  className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                  value={editingItem.intendedAction}
                  onChange={(e) => patch({ intendedAction: e.target.value as PropertyIntendedAction })}
                  disabled={isArchived}
                >
                  {INTENDED_ACTIONS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </label>

              {/* Executor Progress Status */}
              <label className="block text-stone-700">
                Executor Progress Status
                <select
                  className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white"
                  value={editingItem.executorProgressStatus}
                  onChange={(e) => patch({ executorProgressStatus: e.target.value as PropertyExecutorStatus })}
                  disabled={isArchived}
                >
                  {EXECUTOR_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ) : null}
      </Modal>
    </SettlementStepPage>
  );
}
